import { Box, Divider, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';

/**
 * The design language, as four components.
 *
 * These exist because the reference implementation repeats the same `sx` object
 * inline in every file that needs it — the uppercase section label alone appears
 * four times in one 735-line page. Anything that reads as "a card" or "a section
 * heading" in Silverline should come from here, so the look stays consistent and
 * a change lands in one place.
 */

// ─── Section label ──────────────────────────────────────────────────────────

interface SectionLabelProps {
    children: ReactNode;
    /** Omit the rule when the label sits inside a panel rather than over a grid. */
    divider?: boolean;
}

/** Small uppercase letter-spaced heading, optionally over a full-width rule. */
export function SectionLabel({ children, divider = true }: SectionLabelProps): React.JSX.Element {
    return (
        <>
            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 1.5,
                }}
            >
                {children}
            </Typography>
            {divider ? <Divider sx={{ mb: 2 }} /> : null}
        </>
    );
}

// ─── Panel card ─────────────────────────────────────────────────────────────

interface PanelCardProps {
    children: ReactNode;
}

/** Transparent outlined container. The neutral surface for grouped content. */
export function PanelCard({ children }: PanelCardProps): React.JSX.Element {
    return (
        <Paper
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'transparent',
                p: 2,
                height: '100%',
            }}
        >
            {children}
        </Paper>
    );
}

// ─── Stat card ──────────────────────────────────────────────────────────────

export interface StatCardMetric {
    readonly label: string;
    readonly value: string | number;
}

interface StatCardProps {
    title: string;
    icon: ReactNode;
    /** Accent hex. Drives the border, the icon and the numbers. */
    colour: string;
    /** One or two metrics. Two are justified apart, as in the reference design. */
    metrics: readonly StatCardMetric[];
    selected?: boolean;
    onClick?: () => void;
}

/**
 * Accent-bordered statistic card: filled surface, 2px accent border with a
 * heavier left stripe, and large accent-coloured numbers.
 */
export function StatCard({ title, icon, colour, metrics, selected = false, onClick }: StatCardProps): React.JSX.Element {
    const interactive = onClick !== undefined;
    return (
        <Paper
            onClick={onClick}
            sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: interactive ? 'pointer' : 'default',
                bgcolor: 'background.paper',
                border: `2px solid ${alpha(colour, 0.85)}`,
                borderLeftWidth: 4,
                borderBottomWidth: selected ? 4 : 2,
                boxShadow: selected ? `0 4px 18px ${alpha(colour, 0.35)}` : 'none',
                transform: selected ? 'translateY(-1px)' : 'none',
                transition: 'all 0.15s ease',
                ...(interactive && {
                    '&:hover': {
                        boxShadow: `0 4px 18px ${alpha(colour, 0.35)}`,
                        transform: 'translateY(-1px)',
                    },
                }),
            }}
        >
            <Typography
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    mb: 0.75,
                }}
            >
                <Box sx={{ color: colour, display: 'inline-flex' }}>{icon}</Box>
                {title}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary', fontSize: '0.8rem' }}>
                {metrics.map((m) => (
                    <span key={m.label}>{m.label}</span>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.25 }}>
                {metrics.map((m) => (
                    <Typography key={m.label} sx={{ fontSize: '1.6rem', fontWeight: 800, color: colour, lineHeight: 1 }}>
                        {m.value}
                    </Typography>
                ))}
            </Box>
        </Paper>
    );
}

// ─── Module tile ────────────────────────────────────────────────────────────

interface ModuleTileProps {
    label: string;
    description: string;
    icon: ReactNode;
    colour: string;
    onClick: () => void;
    /** Renders dimmed with a "coming soon" note and swallows clicks. */
    available?: boolean;
}

/**
 * Navigation tile: gradient-tinted card with the icon in a rounded square.
 * These replace the legacy sidebar as the primary way into each module.
 */
export function ModuleTile({
    label,
    description,
    icon,
    colour,
    onClick,
    available = true,
}: ModuleTileProps): React.JSX.Element {
    return (
        <Paper
            onClick={available ? onClick : undefined}
            sx={{
                p: 3,
                borderRadius: 2,
                cursor: available ? 'pointer' : 'default',
                border: '1px solid',
                borderColor: alpha(colour, 0.25),
                opacity: available ? 1 : 0.5,
                background: `linear-gradient(135deg, ${alpha(colour, 0.19)} 0%, ${alpha(colour, 0.05)} 100%)`,
                transition: 'all 0.15s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                ...(available && {
                    '&:hover': {
                        borderColor: colour,
                        boxShadow: `0 4px 20px ${alpha(colour, 0.2)}`,
                        transform: 'translateY(-2px)',
                        background: `linear-gradient(135deg, ${alpha(colour, 0.27)} 0%, ${alpha(colour, 0.1)} 100%)`,
                    },
                }),
            }}
        >
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: alpha(colour, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colour,
                }}
            >
                {icon}
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                    {label}
                    {!available ? (
                        <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.6, fontWeight: 400 }}>
                            coming soon
                        </Typography>
                    ) : null}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
        </Paper>
    );
}
